import { analyticsClient } from '@databases/analytics';
import { type DatabaseDiff, type YDatabase } from '@databases/collab/y-database';
import { collabStateManager } from '@databases/collab-state-manager';

import { originIsAgent } from '@canvas/transaction';

import { type Recyclable } from './types';

type Unsubscribe = () => void;
type DatabaseTransaction = Parameters<YDatabase['getDiff']>[0];
type LoadedDatabaseManager = {
  onLoaded(handler: (database: YDatabase) => void): Unsubscribe;
};

export const isDatabaseContentEdit = (diff: DatabaseDiff): boolean =>
  diff.fieldsOrderingChanged ||
  diff.entriesOrderingChanged ||
  diff.changedFieldIds.length > 0 ||
  diff.changedEntryIds.length > 0 ||
  diff.viewsChanged ||
  diff.defaultEntryChanged ||
  diff.othersChanged;

export function createFirstEditAnalyticsRecyclable(): Recyclable {
  let unsubscribeManagerAttached: Unsubscribe | undefined;
  let unsubscribeManagerLoaded: Unsubscribe | undefined;
  let unsubscribeDatabaseUpdates: Unsubscribe | undefined;
  let hasFired = false;

  // We subscribe in layers: manager attached -> manager loaded -> Yjs updates.
  const stopListeningToCurrentManager = () => {
    unsubscribeManagerLoaded?.();
    unsubscribeManagerLoaded = undefined;

    unsubscribeDatabaseUpdates?.();
    unsubscribeDatabaseUpdates = undefined;
  };

  const sendFirstEditAnalyticsEvent = (
    contentId: string,
    transaction: DatabaseTransaction
  ) => {
    hasFired = true;
    unsubscribeDatabaseUpdates?.();
    unsubscribeDatabaseUpdates = undefined;

    void analyticsClient.sendFirstEditEvent({
      contentId,
      isAIGenerated: originIsAgent(transaction.origin),
    });
  };

  const handleDatabaseUpdate = (
    contentId: string,
    database: YDatabase,
    transaction: DatabaseTransaction
  ) => {
    if (hasFired || !transaction.local) {
      return;
    }

    const diff = database.getDiff(transaction);

    if (!isDatabaseContentEdit(diff)) {
      return;
    }

    sendFirstEditAnalyticsEvent(contentId, transaction);
  };

  const listenToDatabaseUpdates = (
    contentId: string,
    database: YDatabase
  ): Unsubscribe => {
    const onUpdate = (
      _update: Uint8Array,
      _origin: unknown,
      _doc: unknown,
      transaction: DatabaseTransaction
    ) => {
      handleDatabaseUpdate(contentId, database, transaction);
    };

    database.doc.on('update', onUpdate);

    return () => database.doc.off('update', onUpdate);
  };

  const listenToManager = (contentId: string, manager: LoadedDatabaseManager) => {
    stopListeningToCurrentManager();

    unsubscribeManagerLoaded = manager.onLoaded((database) => {
      unsubscribeDatabaseUpdates = listenToDatabaseUpdates(contentId, database);
    });
  };

  return {
    setup(contentId: string) {
      unsubscribeManagerAttached = collabStateManager.onCollabManagerAttached(
        contentId,
        (manager) => listenToManager(contentId, manager)
      );
    },

    dispose() {
      unsubscribeManagerAttached?.();
      unsubscribeManagerAttached = undefined;
      stopListeningToCurrentManager();
      hasFired = false;
    },
  };
}
